import {
  assertPublicUrl,
  registerDynamicSource,
  unregisterDynamicSource,
} from "../network/SourceRegistry.ts";
import { NetworkError, type YomiriHttpClient } from "../network/types.ts";
import { Madara } from "../source-engine/templates/Madara.ts";
import { MangaThemesia } from "../source-engine/templates/MangaThemesia.ts";
import type { SourceProfile } from "../source-engine/types.ts";
import { validateProfile } from '../source-engine/ProfileValidator.ts';
import type { RepoSource, RepoExtension } from "./KeiyoushiRepoParser.ts";
import { adaptationFor } from './AdaptedCatalog.ts';
export type InstallationErrorCode = "UNSUPPORTED_ENGINE" | "CHALLENGE_REQUIRED";
export class InstallationError extends Error {
  constructor(
    public readonly code: InstallationErrorCode,
    message: string,
  ) {
    super(message);
  }
}
export function nativeSource(source: RepoSource, pkg: string): string | undefined {
  const adapted = adaptationFor(pkg);
  if (adapted?.id && ['enchiladascan','inmanga','olympusscanlation','novelcool'].includes(adapted.id) && source.lang !== 'es') return undefined;
  if (adapted?.id) return adapted.id;
  let host = "";
  try {
    const rawUrl = source.baseUrl || (source as any).homeUrl || "";
    if (rawUrl) {
      host = new URL(rawUrl).hostname.replace(/^www\./, "");
    }
  } catch {
    // Continue to slug resolution
  }
  const known: Record<string, string> = {
    "mangadex.org": "mangadex",
    "api.mangadex.org": "mangadex",
    "leercapitulo.co": "leercapitulo",
    "leercapitulo.com": "leercapitulo",
    "kazokuden.com": "kazokuden",
    "anzmanga25.com": "anzmanga",
    "dbnewhope.com": "dbnewhope",
    "marmota.me": "marmota",
    "zonatmo.com": "tmo",
    "tumangaonline.com": "tmo",
    "mangaplus.shueisha.co.jp": "mangaplus",
    "bokugents.com": "bokugen",
  };
  if (host && known[host]) return known[host];
  const slug = pkg ? pkg.split(".").pop() : undefined;
  return (
    slug &&
    (
      {
        mangadex: "mangadex",
        leercapitulo: "leercapitulo",
        kazokuden: "kazokuden",
        anzmanga: "anzmanga",
        dbnewhope: "dbnewhope",
        marmota: "marmota",
        tmo: "tmo",
        zonatmo: "tmo",
        tumangaonline: "tmo",
        mangaplus: "mangaplus",
      } as Record<string, string>
    )[slug]
  );
}
export function detectTemplate(html: string): "Madara" | "MangaThemesia" {
  if (/cf-mitigated|<title[^>]*>\s*Just a moment|id=["']challenge-form|cf-chl-/i.test(html))
    throw new InstallationError("CHALLENGE_REQUIRED", "El sitio requiere verificar el navegador.");
  const doc = new DOMParser().parseFromString(html, "text/html");
  if (
    doc.querySelector(
      ".c-tabs-item__content, .page-item-detail.manga, .wp-manga, #manga-chapters-holder",
    ) ||
    /wp-content\/plugins\/madara|wp-manga/i.test(html)
  )
    return "Madara";
  if (
    doc.querySelector(".listupd .bsx, .listupd .bs, .epcheck, #chapterlist") ||
    /ts_reader\.run\(/.test(html)
  )
    return "MangaThemesia";
  throw new InstallationError(
    "UNSUPPORTED_ENGINE",
    "Este sitio no usa una plantilla web compatible.",
  );
}
function fastHash(str: string): string {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

export async function dynamicSourceId(
  source: RepoSource,
  pkg: string,
  repo: string,
): Promise<string> {
  const seed = `${repo}|${pkg}|${source.id}|${source.baseUrl}`;
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const hash = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(seed),
      );
      return `dynamic-${Array.from(new Uint8Array(hash))
        .slice(0, 16)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")}`;
    } catch {
      // Fallback below
    }
  }
  return `dynamic-${fastHash(seed).slice(0, 16)}`;
}
export async function detectSourceProfile(
  source: RepoSource,
  extension: RepoExtension,
  id: string,
  http: Pick<YomiriHttpClient, "text">,
): Promise<SourceProfile> {
  const base = assertPublicUrl(source.baseUrl);
  const allowedHosts = [
    base.hostname,
    base.hostname.startsWith("www.") ? base.hostname.slice(4) : `www.${base.hostname}`,
  ];
  registerDynamicSource({ id, revision: "1", baseUrl: base.href, allowedHosts });
  try {
    if (source.profile) {
      const profile = validateProfile({ ...source.profile, id });
      if (new URL(profile.baseUrl).origin !== base.origin)
        throw new Error('El perfil no coincide con el dominio de la fuente.');
      registerDynamicSource({ id, revision: '1', baseUrl: profile.baseUrl, allowedHosts: profile.allowedHosts });
      return profile;
    }
    const response = await http.text({
      sourceId: id,
      url: base.href,
      method: "GET",
      responseType: "text",
      credentials: "include",
      requiresSession: true,
      cache: { ttlMs: 600000, scope: "session" },
    });
    if (response.classification === "challenge")
      throw new InstallationError(
        "CHALLENGE_REQUIRED",
        "El sitio requiere verificar el navegador.",
      );
    if (response.status >= 400)
      throw new NetworkError("HTTP_ERROR", `Detección: HTTP ${response.status}`, response.status);
    const engine = detectTemplate(response.body);
    const options = {
      id,
      name: source.name,
      baseUrl: base.href.replace(/\/$/, ""),
      allowedHosts,
      ui: {
        idMode: "url" as const,
        mangaPath: "/manga/",
        chapterPath: "/manga/",
        languages: [source.lang],
        trailingSlash: true,
      },
    };
    // Repository icons are UI assets, not hosts authorized for source requests.
    return engine === "Madara"
      ? Madara(options)
      : MangaThemesia({ ...options, ui: { ...options.ui, chapterPath: "/" } });
  } catch (error) {
    unregisterDynamicSource(id);
    if (error instanceof NetworkError && error.code === "CHALLENGE_REQUIRED")
      throw new InstallationError("CHALLENGE_REQUIRED", error.message);
    throw error;
  }
}
